import { useRef } from "react";
import {
    motion,
    useReducedMotion,
    useScroll,
    useTransform,
} from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { EASE } from "../components/Reveal";
import { scrollToId } from "../lib/scroll";
import { trackPhoneClick } from "../lib/analytics";

function MaskedLine({ children, delay }) {
    const reduce = useReducedMotion();
    return (
        <span className="block overflow-hidden pb-1">
            <motion.span
                className="block"
                initial={reduce ? false : { y: "112%" }}
                animate={reduce ? undefined : { y: "0%" }}
                transition={{ duration: 0.95, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

function HexRing({ className, duration = 70, reverse = false }) {
    const reduce = useReducedMotion();
    return (
        <motion.svg
            viewBox="0 0 100 100"
            className={className}
            aria-hidden="true"
            animate={reduce ? undefined : { rotate: reverse ? -360 : 360 }}
            transition={{ duration, repeat: Infinity, ease: "linear" }}
            style={{ transformOrigin: "50% 50%" }}
        >
            <path
                d="M50 3 91 26.5v47L50 97 9 73.5v-47Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
            />
        </motion.svg>
    );
}

export default function Hero() {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const ySlow = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const yFast = useTransform(scrollYProgress, [0, 1], [0, 210]);

    return (
        <section
            id="hero"
            data-testid="hero-section"
            ref={ref}
            className="grain relative overflow-hidden bg-navy pt-[72px]"
        >
            <div className="dot-grid absolute inset-0" aria-hidden="true" />
            <div
                className="absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-royal/25 blur-[140px]"
                aria-hidden="true"
            />

            <div className="relative mx-auto grid max-w-7xl gap-14 px-5 pb-24 pt-16 sm:px-8 lg:grid-cols-12 lg:items-center lg:pb-32 lg:pt-24">
                <div className="lg:col-span-7">
                    <motion.p
                        data-testid="hero-eyebrow"
                        initial={reduce ? false : { opacity: 0, y: 14 }}
                        animate={reduce ? undefined : { opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE }}
                        className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.28em] text-gold"
                    >
                        <span className="h-px w-10 bg-gold/70" aria-hidden="true" />
                        Employer of Record Solutions
                    </motion.p>

                    <h1 className="mt-7 font-display text-[42px] font-extrabold leading-[1.02] tracking-tighter text-white sm:text-6xl lg:text-7xl">
                        <MaskedLine delay={0.15}>Hire Internationally.</MaskedLine>
                        <MaskedLine delay={0.3}>
                            <span className="text-gold">Simplify Employment.</span>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={reduce ? false : { opacity: 0, y: 18 }}
                        animate={reduce ? undefined : { opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
                        className="mt-7 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg"
                    >
                        Explore Employer of Record solutions and international
                        employment services for your business. Hexagon helps
                        businesses evaluate employment administration, employee
                        onboarding, and workforce management options in
                        supported markets, with a primary focus on the United
                        States and additional service availability in Canada.
                    </motion.p>

                    <motion.div
                        initial={reduce ? false : { opacity: 0, y: 18 }}
                        animate={reduce ? undefined : { opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="tel:+18333439367"
                            data-testid="hero-call-cta"
                            onClick={() => trackPhoneClick("hero")}
                            className="inline-flex items-center gap-2.5 rounded-full bg-gold px-7 py-4 font-display text-sm font-extrabold uppercase tracking-wider text-navy transition-[background-color,transform] duration-200 hover:bg-[#b8985a] hover:scale-[1.03]"
                        >
                            <Phone size={16} strokeWidth={2.5} aria-hidden="true" />
                            Call Our EOR Team
                        </a>
                        <button
                            type="button"
                            data-testid="hero-explore-cta"
                            onClick={() => scrollToId("services")}
                            className="inline-flex items-center gap-2.5 rounded-full border border-white/25 px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition-[background-color,border-color] duration-200 hover:border-white/50 hover:bg-white/5"
                        >
                            Explore Our Services
                            <ArrowDown size={16} aria-hidden="true" />
                        </button>
                    </motion.div>

                    <motion.p
                        initial={reduce ? false : { opacity: 0 }}
                        animate={reduce ? undefined : { opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="mt-7 text-sm text-white/55"
                    >
                        Speak with our team directly:{" "}
                        <a
                            href="tel:+18333439367"
                            data-testid="hero-phone-link"
                            onClick={() => trackPhoneClick("hero_phone_text")}
                            className="font-semibold text-white underline decoration-gold/60 underline-offset-4 transition-colors duration-200 hover:text-gold"
                        >
                            +1 833-343-9367
                        </a>
                    </motion.p>
                </div>

                <div className="relative hidden lg:col-span-5 lg:block" aria-hidden="true">
                    <motion.div
                        style={{ y: reduce ? 0 : ySlow }}
                        initial={reduce ? false : { opacity: 0, scale: 0.92 }}
                        animate={reduce ? undefined : { opacity: 1, scale: 1 }}
                        transition={{ duration: 1.1, delay: 0.4, ease: EASE }}
                        className="relative mx-auto aspect-square max-w-[440px]"
                    >
                        <HexRing className="absolute inset-0 text-white/15" duration={80} />
                        <HexRing className="absolute inset-[12%] text-gold/40" duration={55} reverse />
                        <HexRing className="absolute inset-[26%] text-white/25" duration={40} />
                        <div className="absolute inset-[38%] flex items-center justify-center">
                            <svg viewBox="0 0 34 38" className="h-full w-full">
                                <path
                                    d="M17 1.5 31.5 9.75v18.5L17 36.5 2.5 28.25V9.75Z"
                                    fill="#2447A8"
                                />
                                <path
                                    d="M12.5 13v12M21.5 13v12M12.5 19h9"
                                    stroke="#FFFFFF"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M17 5 28.5 11.5 17 18 5.5 11.5Z"
                                    fill="#C8A96B"
                                    opacity="0.9"
                                />
                            </svg>
                        </div>
                        <motion.div
                            style={{ y: reduce ? 0 : yFast }}
                            className="absolute -left-6 top-10 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 backdrop-blur-md"
                        >
                            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
                                Primary Market
                            </p>
                            <p className="mt-1 font-display text-sm font-bold text-white">
                                United States
                            </p>
                        </motion.div>
                        <motion.div
                            style={{ y: reduce ? 0 : ySlow }}
                            className="absolute -right-4 bottom-12 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 backdrop-blur-md"
                        >
                            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/50">
                                Secondary Market
                            </p>
                            <p className="mt-1 font-display text-sm font-bold text-white/80">
                                Canada
                            </p>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
